import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Play, Sparkles, Heart, Star, BookOpen, Users, Wand2, TrendingUp, Award, MessageCircle, Plus, Minus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadCapture from "@/components/LeadCapture";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1778640331184-dc4c3e2608e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBhaXJibmIlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg1MjczMDI1fDA&ixlib=rb-4.1.0&q=85";
const KRISTY_IMG = "/kristy-hero.jpg";

const STATS = [
  { value: "5,000+", label: "Nights Booked" },
  { value: "700+", label: "5-Star Reviews" },
  { value: "4.9", label: "Star Rating" },
  { value: "Hundreds", label: "Owners Helped" },
];

const FEATURE_CATS = [
  { icon: BookOpen, title: "40+ Course Chapters", copy: "From your first listing to franchising your STR business — every stage covered." },
  { icon: Wand2, title: "AI Tools for Hosts", copy: "Generate listings, guest messages, owner pitches and more in seconds." },
  { icon: Users, title: "A Community That Books", copy: "Real hosts, real numbers, real wins. Get answers from operators, not gurus." },
  { icon: TrendingUp, title: "Revenue Playbooks", copy: "PriceLabs configs, gap-night tactics, direct booking — built to raise ADR." },
];

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data.filter((c) => c.featured).slice(0, 3))).catch(() => {});
    api.get("/products?tier=digital").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden bg-neutral-950 text-white">
        <img src={HERO_IMG} alt="Luxury Airbnb interior" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10">
          <Navbar darkOverlay />
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-32">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 animate-fade-up">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur mb-8">
                  <Sparkles className="w-3.5 h-3.5 text-brand-pink" />
                  <span className="text-xs tracking-widest uppercase text-white/80">Powered by BNB Co-Host Academy</span>
                </div>
                <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-balance">
                  Learn how to build income through <span className="italic text-brand-pink font-script text-6xl md:text-7xl lg:text-8xl">Airbnb</span>.
                </h1>
                <p className="mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
                  Whether you want to become a host, co-host, property manager, or launch your own vacation rental business — Kristy will teach you the exact system she built running short-term rentals across Texas.
                </p>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8 py-6 btn-pink-glow" data-testid="hero-start-learning-btn">
                    <Link to="/register">Start Learning <ArrowRight className="ml-2 w-4 h-4" /></Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 px-8 py-6" data-testid="hero-browse-courses-btn">
                    <Link to="/courses">Browse Courses</Link>
                  </Button>
                  <Button asChild size="lg" variant="ghost" className="rounded-full text-white hover:bg-white/10 px-6 py-6" data-testid="hero-watch-training-btn">
                    <Link to="/courses"><BookOpen className="mr-2 w-4 h-4" /> Browse Free Lessons</Link>
                  </Button>
                </div>
                <LeadCapture />
              </div>

              <div className="lg:col-span-5 relative hidden lg:block">
                <div className="relative rounded-[2rem] overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl p-3 shadow-2xl">
                  <img src={KRISTY_IMG} alt="Host Hustle brand" className="rounded-3xl w-full" />
                </div>
                <div className="absolute -bottom-8 -left-8 rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl p-5 flex items-center gap-3 shadow-2xl">
                  <div className="w-11 h-11 rounded-full bg-brand-pink/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-brand-pink" fill="#E91E63" />
                  </div>
                  <div>
                    <div className="font-display text-2xl">4.9</div>
                    <div className="text-xs uppercase tracking-widest text-white/60">Superhost Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social proof strip */}
          <div className="relative border-t border-white/10 bg-black/30 backdrop-blur-lg">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
              {STATS.map((s) => (
                <div key={s.label} className="text-center md:text-left" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-display text-3xl md:text-4xl text-white">{s.value}</div>
                  <div className="text-xs tracking-widest uppercase text-white/60 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU LEARN */}
      <section className="py-24 bg-brand-alabaster">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-4">The Curriculum</div>
            <h2 className="font-display text-4xl md:text-5xl text-neutral-900">Not another guru course.<br />An <span className="italic text-brand-pink">operating system</span> for hosts.</h2>
            <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
              Every module is built from the exact playbooks Kristy used to grow a real property management company — with contracts, SOPs, pricing configs and hiring systems included.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {FEATURE_CATS.map((f, i) => (
              <div key={f.title} data-testid={`feature-${i}`} className="rounded-3xl bg-white p-8 card-soft hover:-translate-y-1 transition-transform duration-300 border border-neutral-100">
                <div className="w-12 h-12 rounded-2xl bg-brand-blush flex items-center justify-center mb-6">
                  <f.icon className="w-5 h-5 text-brand-pink" />
                </div>
                <h3 className="font-display text-xl mb-3">{f.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{f.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Signature Courses</div>
              <h2 className="font-display text-4xl md:text-5xl text-neutral-900">Start where you are.</h2>
            </div>
            <Link to="/courses" data-testid="home-view-all-courses" className="hidden md:inline-flex items-center gap-2 text-brand-pink font-medium">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((c) => (
              <Link
                to={`/courses/${c.id}`}
                key={c.id}
                data-testid={`home-course-${c.id}`}
                className="group rounded-3xl overflow-hidden bg-neutral-50 hover:-translate-y-1 transition-transform duration-300 flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-pink mb-3">
                    <Heart className="w-3 h-3" fill="#E91E63" /> {c.category}
                  </div>
                  <h3 className="font-display text-2xl mb-2">{c.title}</h3>
                  <p className="text-sm text-neutral-600 mb-4">{c.tagline}</p>

                  {c.modules?.length > 0 && (
                    <div className="mb-5 rounded-2xl bg-white border border-neutral-100 p-4">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Inside this course</div>
                      <ul className="space-y-1.5">
                        {c.modules.slice(0, 3).map((m, i) => (
                          <li key={m.id} className="text-xs text-neutral-700 flex items-start gap-2">
                            <span className="font-display text-[11px] text-brand-pink leading-5 w-4 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                            <span className="truncate">{m.title}</span>
                          </li>
                        ))}
                        {c.modules.length > 3 && (
                          <li className="text-[11px] text-neutral-400 pl-6">+ {c.modules.length - 3} more modules</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="mt-auto flex justify-between items-center text-sm">
                    <span className="text-neutral-500">{c.chapters_count} chapters · {c.duration_hrs}h</span>
                    <span className="font-medium text-neutral-900">{c.price === 0 ? "Free" : `$${c.price}`}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS TEASER */}
      <section className="py-24 bg-brand-blush/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">The Storefront</div>
            <h2 className="font-display text-4xl md:text-5xl text-neutral-900 mb-6">Ready-to-run business assets.</h2>
            <p className="text-neutral-700 leading-relaxed mb-8">
              Templates, SOPs, scripts, and mini-courses used inside a real STR management company. Buy the exact assets Kristy uses today.
            </p>
            <Button asChild size="lg" className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white px-8" data-testid="home-shop-btn">
              <Link to="/products">Shop the storefront <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {products.map((p, i) => (
              <div key={p.id} className={`rounded-3xl bg-white p-6 card-soft border border-white ${i % 2 === 0 ? "md:mt-6" : ""}`}>
                <div className="text-xs tracking-widest uppercase text-brand-pink mb-3">Digital Product</div>
                <div className="font-display text-lg mb-2">{p.name}</div>
                <div className="text-xs text-neutral-500 mb-6 line-clamp-2">{p.description}</div>
                <div className="flex justify-between items-center">
                  <span className="font-display text-xl">${p.price}</span>
                  <Link to="/products" className="text-brand-pink text-sm inline-flex items-center gap-1">Get it <ArrowRight className="w-3 h-3" /></Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT KRISTY */}
      <section className="py-24 bg-neutral-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img
              src={KRISTY_IMG}
              alt="Kristy at her Host Hustle desk"
              className="rounded-[2rem] w-full aspect-[4/5] object-cover border border-white/10"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Meet Your Instructor</div>
            <h2 className="font-display text-4xl md:text-5xl mb-6">From nurse to <span className="italic text-brand-pink">San Antonio operator</span>.</h2>
            <p className="text-white/70 leading-relaxed text-lg mb-8">
              Kristy left her nursing career, started with short-term rental arbitrage in Texas, cleaned every turnover herself, built her own cleaning company, and rolled it all into a full property management business across San Antonio. Now she teaches the exact frameworks — scripts, SOPs, contracts, hiring systems — to a new generation of hosts.
            </p>
            <div className="grid grid-cols-2 gap-6">
              {[
                { k: "700+", v: "Owner Conversations" },
                { k: "12", v: "Team Members" },
                { k: "5,000+", v: "Guest Nights" },
                { k: "9 yrs", v: "In the Trenches" },
              ].map((s) => (
                <div key={s.v} className="border-l border-white/15 pl-4">
                  <div className="font-display text-3xl">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
            <Button asChild size="lg" className="mt-10 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8" data-testid="home-about-btn">
              <Link to="/about">Kristy's Story <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-brand-alabaster">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-2xl mx-auto">
              <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Student Wins</div>
              <h2 className="font-display text-4xl md:text-5xl">Real hosts. <span className="italic text-brand-pink">Real results.</span></h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-14">
              {testimonials.map((t) => (
                <div key={t.id} data-testid={`testimonial-${t.id}`} className="rounded-3xl bg-white p-8 border border-neutral-100 card-soft">
                  <div className="flex gap-1 mb-4">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 text-brand-pink" fill="#E91E63" />)}</div>
                  <p className="text-neutral-800 leading-relaxed mb-6">"{t.quote}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-neutral-100">
                    <div className="w-10 h-10 rounded-full bg-brand-blush flex items-center justify-center text-brand-pink font-medium">{t.name[0]}</div>
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-neutral-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* COACHING TEASER */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Host Hustle Coaching</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mb-6">Want Kristy <span className="italic">in your corner</span>?</h2>
            <p className="text-neutral-600 leading-relaxed mb-8">
              Book a 1:1 STR Strategy Session to unlock what's stuck, or apply for the 12-week Co-Host Mentorship to rebuild your business with Kristy's private coaching.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow" data-testid="home-coaching-btn">
                <Link to="/coaching">Book coaching <ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-neutral-300" data-testid="home-toolbox-btn">
                <Link to="/toolbox">Open the Toolbox</Link>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: MessageCircle, k: "1:1", v: "Strategy Sessions" },
              { icon: Users, k: "12 wk", v: "Mentorship Program" },
              { icon: Award, k: "40+", v: "Chapters, Templates, SOPs" },
              { icon: Sparkles, k: "AI", v: "Ask Host Hustle 24/7" },
            ].map((s, i) => (
              <div key={i} className="rounded-3xl bg-brand-blush/50 p-6">
                <s.icon className="w-6 h-6 text-brand-pink mb-4" />
                <div className="font-display text-3xl">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-600 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="py-24 bg-brand-alabaster">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">FAQ</div>
              <h2 className="font-display text-4xl md:text-5xl">Questions worth asking.</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id} data-testid={`faq-${f.id}`} className="rounded-2xl bg-white border border-neutral-100 px-6">
                  <AccordionTrigger className="hover:no-underline py-5 font-display text-lg text-left">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-neutral-600 pb-5 leading-relaxed">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="font-script text-3xl text-brand-pink mb-6">Let's hustle smarter, together.</div>
          <h2 className="font-display text-4xl md:text-6xl mb-8">Your <span className="italic">Airbnb business</span> starts today.</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-10 py-6 btn-pink-glow" data-testid="cta-join-btn">
              <Link to="/register">Join Host Hustle</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-neutral-300 px-10 py-6" data-testid="cta-cohost-btn">
              <Link to="/courses">Become a Co-Host</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
